import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { PageHeaderComponent } from '@core/components/page-header/page-header.component';
import {
  INTEGRATION_CATALOG,
  type IntegrationCatalogCategory,
  type IntegrationCatalogItem,
} from '../forms/utils/form-integration-catalog';

export type IntegrationAppFilter = 'all' | 'connected' | 'available';
export type IntegrationCategoryFilter = 'all' | IntegrationCatalogCategory;
export type IntegrationViewMode = 'grid' | 'list';

@Component({
  selector: 'app-integrations-page',
  standalone: true,
  imports: [NgTemplateOutlet, PageHeaderComponent],
  templateUrl: './integrations-page.html',
  styleUrl: './integrations-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IntegrationsPage {
  readonly catalog = INTEGRATION_CATALOG;

  readonly appFilter = signal<IntegrationAppFilter>('all');
  readonly categoryFilter = signal<IntegrationCategoryFilter>('all');
  readonly viewMode = signal<IntegrationViewMode>('grid');

  readonly connectedIds = signal<Set<string>>(
    new Set(['google-calendar', 'slack', 'hubspot', 'zoom']),
  );

  readonly filteredIntegrations = computed(() => {
    const app = this.appFilter();
    const category = this.categoryFilter();
    const connected = this.connectedIds();

    return this.catalog.filter((item) => {
      if (category !== 'all' && item.category !== category) {
        return false;
      }
      const isConnected = connected.has(item.id);
      if (app === 'connected') {
        return isConnected;
      }
      if (app === 'available') {
        return !isConnected;
      }
      return true;
    });
  });

  isConnected(id: string): boolean {
    return this.connectedIds().has(id);
  }

  toggleConnection(item: IntegrationCatalogItem): void {
    this.connectedIds.update((current) => {
      const next = new Set(current);
      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        next.add(item.id);
      }
      return next;
    });
  }

  onAppFilterChange(event: Event): void {
    this.appFilter.set((event.target as HTMLSelectElement).value as IntegrationAppFilter);
  }

  onCategoryFilterChange(event: Event): void {
    this.categoryFilter.set(
      (event.target as HTMLSelectElement).value as IntegrationCategoryFilter,
    );
  }

  setViewMode(mode: IntegrationViewMode): void {
    this.viewMode.set(mode);
  }
}
